import type { DecodeResult, PersistedEnvelope, PersistedSliceDescriptor } from "../types";

/**
 * Parses unknown data through the slice schema, falling back to `slice.default()` on failure.
 *
 * @typeParam TValue Slice value type.
 * @param slice Slice descriptor providing schema and defaults.
 * @param candidate Raw `data` field or `undefined` when the envelope is missing.
 * @returns Validated value or schema defaults.
 */
const parseOrFallback = <TValue>(
    slice: PersistedSliceDescriptor<TValue>,
    candidate: unknown,
): TValue => {
    const result = slice.schema.safeParse( candidate );

    return result.success ? result.data : slice.default();
};

/**
 * Decodes a persisted envelope into the current slice shape with tolerant forward/backward handling.
 *
 * @typeParam TValue Slice value type.
 * @param slice Slice descriptor including schema, version, and migrations.
 * @param raw Stored envelope, or `null` when the key is absent.
 * @returns Decoded value plus flags for migration and future-version retention.
 */
export const decodeEnvelope = <TValue>(
    slice: PersistedSliceDescriptor<TValue>,
    raw: PersistedEnvelope<unknown> | null,
): DecodeResult<TValue> => {
    if ( raw === null ) {
        return {
            "value":               parseOrFallback( slice, undefined ),
            "isFromFuturePayload": false,
            "wasUpgraded":         false,
        };
    }

    if ( raw.version === slice.currentVersion ) {
        return {
            "value":               parseOrFallback( slice, raw.data ),
            "isFromFuturePayload": false,
            "wasUpgraded":         false,
        };
    }

    if ( raw.version < slice.currentVersion ) {
        let cursor: unknown = raw.data;

        // Apply migrations sequentially from (storedVersion + 1) through currentVersion.
        for ( let version = raw.version + 1; version <= slice.currentVersion; version++ ) {
            const migration = slice.migrations[version];
            if ( migration !== undefined ) {
                cursor = migration( cursor );
            }
        }

        return {
            "value":               parseOrFallback( slice, cursor ),
            "isFromFuturePayload": false,
            "wasUpgraded":         true,
        };
    }

    // Newer app downgrade path: best-effort parse, retain raw envelope, block writes until user edit.
    return {
        "value":               parseOrFallback( slice, raw.data ),
        "isFromFuturePayload": true,
        "wasUpgraded":         false,
    };
};
